import type { Id, Match, RuleSet, StandingRow, Tiebreaker } from '../types.js';
import { slotTeamIds } from '../slots.js';

/**
 * Tabellenberechnung (docs/04 §5). Immer aus den Matches neu berechnet.
 *
 * Direkter Vergleich: Bei k > 2 punktgleichen Teams wird eine Sub-Tabelle nur aus
 * den Spielen dieser Teams untereinander gebildet und mit derselben Kette sortiert.
 * Löst sich die Gleichheit nur teilweise, wird die verbleibende Gruppe erneut
 * geprüft; bringt der Vergleich nichts, greift das nächste Kriterium.
 */

interface Stats {
  teamId: Id;
  played: number;
  wins: number;
  draws: number;
  losses: number;
  points: number;
  scoreFor: number;
  scoreAgainst: number;
  setsFor: number;
  setsAgainst: number;
}

export interface StandingsOptions {
  /** Anzahl Qualifikationsplätze (markiert `qualified`) */
  qualifiers?: number;
  /** Los-Entscheidung der Turnierleitung: teamId → Reihenfolge (1 = vorne) */
  lotOrder?: Record<Id, number>;
  /** Spiele gegen diese Teams ignorieren (Streichresultat bei ungleichen Gruppen) */
  excludeOpponents?: Id[];
  /** Pro Spiel werten; ohne Angabe automatisch, wenn ungleich viele Spiele angesetzt sind (`ungleicheSpielzahl`) */
  perGame?: boolean;
}

const LABELS: Record<Tiebreaker, string> = {
  points: 'Punkte',
  headToHead: 'direkter Vergleich',
  scoreDiff: 'Punktdifferenz',
  scoreFor: 'erzielte Punkte',
  wins: 'Siege',
  lot: 'Los',
};

/** Nur Spiele, die für die Tabelle zählen */
export function countableMatches(matches: readonly Match[]): Match[] {
  return matches.filter((m) => (m.status === 'finished' || m.status === 'walkover') && m.result && !m.annulled && m.a.kind === 'team' && m.b.kind === 'team');
}

/** Wer auf einer Seite steht — bei wechselnden Partnern zwei Spieler, die beide gutgeschrieben bekommen */
function idsOf(m: Match, side: 'A' | 'B'): Id[] {
  const ids = slotTeamIds(side === 'A' ? m.a : m.b);
  if (ids.length === 0) throw new Error('match without team');
  return ids;
}

function collectStats(teamIds: readonly Id[], matches: readonly Match[], rules: RuleSet, exclude: ReadonlySet<Id>): Map<Id, Stats> {
  const stats = new Map<Id, Stats>();
  for (const id of teamIds) {
    stats.set(id, { teamId: id, played: 0, wins: 0, draws: 0, losses: 0, points: 0, scoreFor: 0, scoreAgainst: 0, setsFor: 0, setsAgainst: 0 });
  }
  const credit = (st: Stats | undefined, f: number, a: number, setsF: number, setsA: number, outcome: 'win' | 'draw' | 'loss') => {
    if (!st) return;
    st.played++;
    st.scoreFor += f;
    st.scoreAgainst += a;
    st.setsFor += setsF;
    st.setsAgainst += setsA;
    if (outcome === 'win') st.wins++;
    else if (outcome === 'loss') st.losses++;
    else st.draws++;
    st.points += rules.points[outcome];
  };
  for (const m of matches) {
    const a = idsOf(m, 'A');
    const b = idsOf(m, 'B');
    if (!m.result) continue;
    // Gewöhnlich: nur Spiele zwischen zwei Teams dieser Tabelle. Bei Partnern zählt jeder, der hier steht
    if (!a.some((id) => stats.has(id)) || !b.some((id) => stats.has(id))) continue;
    if ([...a, ...b].some((id) => exclude.has(id))) continue;
    let pa = 0;
    let pb = 0;
    let setsA = 0;
    let setsB = 0;
    for (const s of m.result.sets) {
      pa += s.a;
      pb += s.b;
      if (s.a > s.b) setsA++;
      else if (s.b > s.a) setsB++;
    }
    const w = m.result.winner;
    for (const id of a) credit(stats.get(id), pa, pb, setsA, setsB, w === 'A' ? 'win' : w === 'B' ? 'loss' : 'draw');
    for (const id of b) credit(stats.get(id), pb, pa, setsB, setsA, w === 'B' ? 'win' : w === 'A' ? 'loss' : 'draw');
  }
  return stats;
}

function keyOf(s: Stats, c: Tiebreaker, perGame = false): number {
  // Pro Spiel (Quotient), wenn in der Gruppe nicht alle gleich viele Spiele haben — siehe `ungleicheSpielzahl`
  const q = (v: number) => (perGame ? (s.played > 0 ? v / s.played : 0) : v);
  switch (c) {
    case 'points':
      return q(s.points);
    case 'scoreDiff':
      return q(s.scoreFor - s.scoreAgainst);
    case 'scoreFor':
      return q(s.scoreFor);
    case 'wins':
      return q(s.wins);
    case 'headToHead':
    case 'lot':
      return 0;
    default:
      // Unbekanntes Kriterium aus einem alten Regelwerk (z. B. „buchholz"): trennt nichts, die Kette geht weiter
      return 0;
  }
}

function fmt(c: Tiebreaker, v: number): string {
  // Quotienten mit zwei Stellen und deutschem Komma („0,80“, „+2,40“)
  const zahl = Number.isInteger(v) ? String(v) : v.toFixed(2).replace('.', ',');
  if (c === 'scoreDiff') return v > 0 ? `+${zahl}` : zahl;
  return zahl;
}

interface Placed {
  teamId: Id;
  notes: string[];
  needsLot: boolean;
  /** Teams, mit denen dieser Platz ungelöst geteilt wird */
  tiedWith: Id[];
}

/** Sortiert `ids` nach Kette ab Index `ci`; `matches` = Spiele für Sub-Tabellen */
function rank(ids: readonly Id[], ci: number, stats: Map<Id, Stats>, allMatches: readonly Match[], rules: RuleSet, opts: StandingsOptions & { perGame?: boolean }, inherited: string[]): Placed[] {
  if (ids.length === 1) {
    const id = ids[0] as Id;
    return [{ teamId: id, notes: inherited, needsLot: false, tiedWith: [] }];
  }
  const chain = rules.tiebreakers;
  if (ci >= chain.length) {
    // Kette erschöpft, kein Los konfiguriert → ungelöst
    return ids.map((id) => ({ teamId: id, notes: [...inherited, 'Gleichstand nicht auflösbar'], needsLot: true, tiedWith: ids.filter((o) => o !== id) }));
  }
  const c = chain[ci] as Tiebreaker;

  if (c === 'lot') {
    const lot = opts.lotOrder;
    if (lot && ids.every((id) => lot[id] !== undefined)) {
      const sorted = ids.slice().sort((x, y) => (lot[x] as number) - (lot[y] as number));
      return sorted.map((id) => ({ teamId: id, notes: [...inherited, 'Los (Entscheidung Turnierleitung)'], needsLot: false, tiedWith: [] }));
    }
    return ids.map((id) => ({ teamId: id, notes: [...inherited, 'Los erforderlich'], needsLot: true, tiedWith: ids.filter((o) => o !== id) }));
  }

  if (c === 'headToHead') {
    const set = new Set(ids);
    // Bei wechselnden Partnern: nur Spiele, in denen alle vier zu den Gleichen gehören
    const sub = allMatches.filter((m) => idsOf(m, 'A').every((id) => set.has(id)) && idsOf(m, 'B').every((id) => set.has(id)));
    const subStats = collectStats(ids, sub, rules, new Set());
    // Sub-Tabelle: erstes Kriterium der Kette (ohne direkten Vergleich/Los), das
    // untereinander trennt. Das ist die Begründung, die in der Tabelle steht.
    const subChain = chain.filter((x) => x !== 'headToHead' && x !== 'lot');
    for (const sc of subChain) {
      const groups = partitionBy(ids, (id) => keyOf(subStats.get(id) as Stats, sc, opts.perGame));
      if (groups.length === 1) continue;
      const out: Placed[] = [];
      for (const g of groups) {
        const v = keyOf(subStats.get(g[0] as Id) as Stats, sc, opts.perGame);
        const note = `${LABELS.headToHead}: ${LABELS[sc]} ${fmt(sc, v)} untereinander`;
        // Restgruppe erneut ab direktem Vergleich prüfen (kleinere Menge → terminiert)
        out.push(...rank(g, ci, stats, allMatches, rules, opts, [...inherited, note]));
      }
      return out;
    }
    // Kein Fortschritt → nächstes Kriterium mit Gesamtstatistik
    return rank(ids, ci + 1, stats, allMatches, rules, opts, inherited);
  }

  // Numerisches Kriterium
  const groups = partitionBy(ids, (id) => keyOf(stats.get(id) as Stats, c, opts.perGame));
  if (groups.length === 1) return rank(ids, ci + 1, stats, allMatches, rules, opts, inherited);
  const out: Placed[] = [];
  for (const g of groups) {
    const v = keyOf(stats.get(g[0] as Id) as Stats, c, opts.perGame);
    // Pro Spiel gewertet: auch die Punkte-Quote nennen — sonst steht 3 Siege hinter 2 Siegen ohne Erklärung
    const note = opts.perGame ? [`${LABELS[c]} ${fmt(c, v)} (pro Spiel gewertet)`] : c === 'points' ? [] : [`${LABELS[c]} ${fmt(c, v)}`];
    out.push(...rank(g, ci + 1, stats, allMatches, rules, opts, [...inherited, ...note]));
  }
  return out;
}

/** Teilt nach Schlüssel absteigend in Gruppen gleicher Werte, Reihenfolge stabil */
function partitionBy(ids: readonly Id[], key: (id: Id) => number): Id[][] {
  const sorted = ids.slice().sort((x, y) => key(y) - key(x));
  const groups: Id[][] = [];
  for (const id of sorted) {
    const last = groups[groups.length - 1];
    if (last && key(last[0] as Id) === key(id)) last.push(id);
    else groups.push([id]);
  }
  return groups;
}

/**
 * Haben in dieser Gruppe nicht alle gleich viele Spiele **angesetzt**? Etwa bei
 * wechselnden Partnern mit 6 Spielern (2 spielen eins weniger) oder wenn nach
 * einem Rückzug Ergebnisse annulliert sind. Dann wird die Tabelle pro Spiel
 * gewertet: Siege ÷ Spiele, Punktdifferenz ÷ Spiele (Harald/Daniel, 10/2026 —
 * „der Quotient ist wesentlich fairer“). Unterschiede, die nur daher kommen,
 * dass noch nicht alles gespielt ist, zählen nicht: gezählt wird der Plan.
 */
export function ungleicheSpielzahl(teamIds: readonly Id[], matches: readonly Match[]): boolean {
  if (teamIds.length < 2) return false;
  const anzahl = new Map<Id, number>(teamIds.map((id) => [id, 0]));
  for (const m of matches) {
    if (m.status === 'cancelled' || m.annulled) continue;
    for (const id of [...slotTeamIds(m.a), ...slotTeamIds(m.b)]) if (anzahl.has(id)) anzahl.set(id, (anzahl.get(id) as number) + 1);
  }
  return new Set(anzahl.values()).size > 1;
}

export function computeStandings(teamIds: readonly Id[], matches: readonly Match[], rules: RuleSet, opts: StandingsOptions = {}): StandingRow[] {
  const counted = countableMatches(matches);
  const stats = collectStats(teamIds, counted, rules, new Set(opts.excludeOpponents ?? []));
  const perGame = opts.perGame ?? ungleicheSpielzahl(teamIds, matches);
  const placed = rank(teamIds, 0, stats, counted, rules, { ...opts, perGame }, []);

  const rows: StandingRow[] = [];
  let i = 0;
  while (i < placed.length) {
    const p = placed[i] as Placed;
    // Ungelöste Gleichstände teilen sich den Rang
    const tieSize = p.needsLot ? p.tiedWith.length + 1 : 1;
    for (let k = 0; k < tieSize && i + k < placed.length; k++) {
      const q = placed[i + k] as Placed;
      const s = stats.get(q.teamId) as Stats;
      const rankNo = i + 1;
      rows.push({
        teamId: q.teamId,
        rank: rankNo,
        played: s.played,
        wins: s.wins,
        draws: s.draws,
        losses: s.losses,
        points: s.points,
        scoreFor: s.scoreFor,
        scoreAgainst: s.scoreAgainst,
        scoreDiff: s.scoreFor - s.scoreAgainst,
        setsFor: s.setsFor,
        setsAgainst: s.setsAgainst,
        tiebreakNotes: q.notes,
        needsLot: q.needsLot,
        ...(perGame ? { perGame: true as const } : {}),
        qualified: opts.qualifiers !== undefined && rankNo <= opts.qualifiers && !q.needsLot,
      });
    }
    i += tieSize;
  }
  return rows;
}

export interface CrossGroupCandidate {
  row: StandingRow;
  groupId: Id;
}

/**
 * Gruppenübergreifender Vergleich (beste Zweite/Dritte). Direkter Vergleich
 * und Los entfallen; Reihenfolge der übrigen Kriterien wie konfiguriert.
 * Gibt Kandidaten sortiert zurück, mit Begründung.
 */
export function rankAcrossGroups(candidates: CrossGroupCandidate[], rules: RuleSet): { candidate: CrossGroupCandidate; notes: string[] }[] {
  // Harald (10/2026): bei gleich vielen Siegen zählt über die Gruppen hinweg die
  // Punktdifferenz, nicht die erzielten Punkte — Differenz immer direkt nach den Punkten
  const basis = rules.tiebreakers.filter((c) => c !== 'headToHead' && c !== 'lot');
  const chain: Tiebreaker[] = [...basis.filter((c) => c === 'points'), 'scoreDiff', ...basis.filter((c) => c !== 'points' && c !== 'scoreDiff')];
  const keyOfRow = (r: StandingRow, c: Tiebreaker): number => {
    switch (c) {
      case 'points':
        return r.points;
      case 'scoreDiff':
        return r.scoreDiff;
      case 'scoreFor':
        return r.scoreFor;
      case 'wins':
        return r.wins;
      default:
        return 0;
    }
  };
  // Weniger Spiele (ungleiche Gruppen) → pro Spiel normalisieren, sonst verzerrt
  const perGame = candidates.some((c) => c.row.played !== (candidates[0] as CrossGroupCandidate).row.played);
  const val = (r: StandingRow, c: Tiebreaker): number => {
    const v = keyOfRow(r, c);
    return perGame && r.played > 0 ? v / r.played : v;
  };
  const vergleich = (x: CrossGroupCandidate, y: CrossGroupCandidate): number => {
    for (const c of chain) {
      const d = val(y.row, c) - val(x.row, c);
      if (Math.abs(d) > 1e-9) return d;
    }
    return 0;
  };
  const sorted = candidates.slice().sort(vergleich);
  // Bis zu welchem Kriterium musste verglichen werden, um vom Nachbarn getrennt zu sein?
  const tiefe = (a: CrossGroupCandidate, b: CrossGroupCandidate | undefined): number => {
    if (!b) return 0;
    const i = chain.findIndex((c) => Math.abs(val(a.row, c) - val(b.row, c)) > 1e-9);
    return i < 0 ? chain.length - 1 : i;
  };
  return sorted.map((candidate, i) => {
    const bis = Math.max(1, tiefe(candidate, sorted[i - 1]), tiefe(candidate, sorted[i + 1]));
    const notes: string[] = [];
    // Punkte stehen in der Anzeige daneben; danach die Kriterien, die tatsächlich entschieden haben (mindestens die Differenz)
    for (const c of chain.slice(0, bis + 1)) {
      if (c === 'points') continue;
      const v = perGame ? val(candidate.row, c) : keyOfRow(candidate.row, c);
      notes.push(`${LABELS[c]} ${fmt(c, v)}${perGame ? ' (pro Spiel gewertet)' : ''}`);
    }
    return { candidate, notes };
  });
}
